import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCube, faMessageLines, faPencil, faVideo } from '@fortawesome/pro-duotone-svg-icons';
import { faTableColumns, faWindowMaximize } from '@fortawesome/duotone-regular-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';

import { config } from '../../../../../environments/environment';
import { TPanelSections } from '../../../../state/app.repository';
import { MessageStore } from '../../../../state/messages.repository';
import { UserSettingsStore } from '../../../../state/user-settings.repository';
import { TooltipDirective } from '../../../../shared/components/ui/tooltip/tooltip.directive';
import { AppService } from '../../../services/app.service';

interface ModuleOption {
  id: TPanelSections;
  icon: IconDefinition;
  label: string;
}

const MODULES: ModuleOption[] = [
  { id: 'video', icon: faVideo, label: 'Video Chat' },
  { id: 'chat', icon: faMessageLines, label: 'Text Chat' },
  { id: 'whiteboard', icon: faPencil, label: 'Whiteboard' },
  { id: 'spatial', icon: faCube, label: '3D Audio' },
];

/**
 * The conference panel's actions as a horizontal bar above the components
 * frame in `.main`: the 1↔2 column toggle, a separator, then the main-module
 * buttons. The module buttons carry their title next to the icon once the bar
 * is wide enough. The pinned module is not offered as main, the chat badge
 * hides while chat is the main module, and 3D is test-only.
 */
@Component({
  selector: 'andor-panel-nav-bar',
  standalone: true,
  imports: [FaDuotoneIconComponent, TranslatePipe, TooltipDirective],
  templateUrl: './panel-nav-bar.html',
  styleUrl: './panel-nav-bar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelNavBar {
  private readonly _userSettings = inject(UserSettingsStore);

  protected readonly faTableColumns = faTableColumns;
  protected readonly faWindowMaximize = faWindowMaximize;

  protected readonly main = toSignal(this._userSettings.panelClassMain$, {
    initialValue: this._userSettings.value.panelClassMain,
  });
  protected readonly pinned = toSignal(this._userSettings.panelClassPinned$, {
    initialValue: this._userSettings.value.panelClassPinned,
  });
  private readonly _unread = toSignal(inject(MessageStore).currentRoomUnreadCount$, {
    initialValue: 0,
  });
  private readonly _breakpoint = toSignal(inject(AppService).activeBreakpoint$, {
    initialValue: 'small' as const,
  });

  protected readonly modules = computed(() =>
    MODULES.filter(
      (m) => m.id !== this.pinned() && (m.id !== 'spatial' || config.showTestComponents)
    )
  );
  /** Unread badge on chat, hidden while chat is what the stage shows. */
  protected readonly chatBadge = computed(() => {
    const count = this.main() === 'chat' ? 0 : this._unread();
    return count > 99 ? '99+' : count > 0 ? String(count) : '';
  });
  // The pinned column renders from the TS 'medium' breakpoint up; the app
  // service drops the pin below it.
  protected readonly canPin = computed(() => this._breakpoint() !== 'small');

  protected open(id: TPanelSections) {
    this._userSettings.update({ panelClassMain: id });
  }

  protected togglePin() {
    this._userSettings.togglePinnedModule();
  }
}
